import { getFeedsComments, getFeedsSearchComments, prepareFeedsCosDownload, pollFeedsCosDownloadStatus } from '@op/services';
import { DownloadConfigInfoType } from '../types';
import {
  getAllLogValue,
  getLogValue,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import { getFeedsReviewsType } from '@op/utils/text';
import {
  commentType,
  monitorSourceName,
  hideSentimentFunc,
  renderHelperLiveBroadcastViews,
  tryTransNumber,
  biggerOrTo,
  languageRenderHelper,
  gameStoreRating,
  reviewerPlaytime,
  regionRenderHelper,
  renderMonitorSource,
  formatTimeToFormat,
  gameStoreExtJsonHelper,
  newsBakContent,
  renderIsValid,
} from '../renderHelper';
import { equalAndTo } from '../renderHelper/default';

// 游戏商店相关辅助函数
const refundedMap: any = {
  0: 'No',
  1: 'Yes',
};

function dealwithSteamStore(value: number) {
  return value === 1 ? 'Yes' : 'No';
}

function dealwithSteamStarred(value: number) {
  return value === 1 ? 'No' : 'Yes';
}

// export const FeedsSocialMediaDownload: DownloadConfigInfoType = {
//   type: 'file',
//   downloadFunc: (parmas: any) => downloadFeedsComments({ ...parmas, "download_mode": "excel" }),
//   // mimeType: 'text/csv; charset=UTF-8',
// };

// COS 异步下载配置
export const FeedsSocialMediaDownload: DownloadConfigInfoType = {
  type: 'cos',
  prepareDownloadFunc: (filter, config) => {
    const {fileName} = config;
    return prepareFeedsCosDownload({...filter, file_title: fileName});
  },
  pollDownloadFunc: (pollParams) => pollFeedsCosDownloadStatus(pollParams),
  pollConfig: {
    interval: 3000,
    maxRetries: 60,
    // http() 已自动解构 data 层，直接用 job_id
    prepareResponseExtractor: (res) => res?.job_id,
    pollRequestBuilder: (jobId) => ({ job_id: jobId }),
    // http() 已自动解构 data 层，直接用 status/url
    pollResponseHandler: (res) => {
      const status = res?.status;
      if (status === 'success' && res?.url) {
        return { status: 'success', url: res.url };
      }
      if (status === 'failed') {
        return { status: 'failed' };
      }
      return { status: 'pending' };
    },
  },
};

// 社交媒体精准/拓展搜索下载配置（前端分页下载）
export const FeedsSocialMediaSearchDownload: DownloadConfigInfoType = {
  type: 'page',
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { hideSentimentRating, isExactOrExtendSearch } = auxiliaryParams;
    // 精准/拓展搜索使用 search 接口，模糊搜索/无搜索（总量>=20K 走前端分页）使用 comments 接口
    const data = isExactOrExtendSearch
      ? await getFeedsSearchComments(filter)
      : await getFeedsComments(filter);
    const { list = [], total } = data;
    const sentimentMap = await getAllLogValue(
      list,
      {},
      { value_type: ['sentiment', 'topic', 'valid'] },
    );

    const dataList = list.map(
      (item: {
        content: string;
        content_to_en: string;
        content_to_zh: string;
        sentiment_rating: any;
        topic: string;
        isvalid: string;
      }) => {
        const [newSentiment, newTopic, newValid] = [
          'latest_sentiment',
          'latest_topic',
          'latest_valid',
        ].map(key => getLogValue(sentimentMap, item, key as any));

        const sentimentDefaultValue = hideSentimentRating
          ? null
          : item.sentiment_rating;

        // 处理 topic，使用 | 切割后组装成 topic_1 - topic_30
        const finalTopic = newTopic || item.topic || '';
        const topicList = finalTopic.split('|').filter(t => t.trim());
        const topicFields: Record<string, string> = {};
        for (let i = 1; i <= 30; i++) {
          topicFields[`topic_${i}`] = topicList[i - 1] || '';
        }

        return {
          ...item,
          sentiment_rating: newSentiment || sentimentDefaultValue,
          isvalid: newValid || item.isvalid,
          topic: finalTopic,
          ...topicFields,
        };
      },
    );
    return {
      list: dataList,
      total,
    };
  },
  columns: ({ auxiliaryParams }) => {
    const { metrics, hideSentimentRating, channelList = [] } = auxiliaryParams;

    // 判断是否显示 Live Broadcast Views 列（仅当 channelList 包含 chzzk 时显示）
    const hasChzzk = channelList.includes('chzzk');

    let allColumns = [
      {
        key: 'comment_parent_id',
        header: `Parent_comment_id
(Only replies have Parent_comment_id, which indicates the id of the post.
Use the reply's Parent_comment_id to find all the replies of the post.
Or use the comment_id of the post to find all the replies for that post.)`,
        renderHelper: [tryTransNumber, equalAndTo('-1', '')],
      },
      {
        key: 'parent_comment_url',
        header: 'Parent Comment Url',
      },
      {
        key: 'comment_id',
        header: `Comment_id (Only posts have Comment_id)`,
        renderHelper: [tryTransNumber, equalAndTo('-1', '')],
      },
      {
        key: 'comment_type',
        header: 'Comment_type',
        renderHelper: [commentType],
      },
      {
        key: 'comment_uin',
        header: 'comment_uin',
      },
      {
        key: 'channel_name',
        header: 'channel',
      },
      {
        key: 'monitor_source',
        header: 'Monitor Source',
        renderHelper: [monitorSourceName],
      },
      {
        key: 'reviews_type',
        header: 'Review Type',
        renderHelper: [getFeedsReviewsType],
      },
      {
        key: 'sentiment_rating',
        header: 'Sentiment',
        renderHelper: [hideSentimentFunc(hideSentimentRating)],
        numFmt: ['0.00'],
      },
      {
        key: 'reviewer',
        header: 'Reviewer',
      },
      {
        key: 'content',
        header: 'Content_Original',
      },
      {
        key: 'content_to_zh',
        header: 'Content_Chinese',
      },
      {
        key: 'content_to_en',
        header: 'Content_English',
      },
      {
        key: 'isvalid',
        header: 'Validation',
        renderHelper: [renderIsValid],
      },
      {
        key: 'content_url',
        header: 'URL',
      },
      {
        key: 'comment_time',
        header: 'Date',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD')],
      },
      {
        key: 'comment_time',
        header: 'Time(UTC+8:00)',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD HH:mm:ss')],
      },
      {
        key: 'language',
        header: 'Language',
        renderHelper: [languageRenderHelper],
      },
      {
        key: 'engagement',
        header: 'Engagement',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'tweets_like',
        header: 'Like',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'tweets_unlike',
        header: 'Dislike',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'tweets_retweet',
        header: 'Share',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'tweets_reply',
        header: 'Comment',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'view',
        header: 'View',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      {
        key: 'subscribe',
        header: 'Subscribe',
        numFmt: '#,##',
        renderHelper: [biggerOrTo(0, '')],
      },
      // Live Broadcast Views 仅当 channelList 包含 chzzk 时显示
      {
        key: 'live_count',
        header: 'Live Broadcast Views',
        numFmt: '#,##',
        renderHelper: [renderHelperLiveBroadcastViews],
      },
      {
        key: 'description',
        header: 'Description',
      },
      {
        key: 'ctgr_1',
        header: 'CTGR1',
      },
      {
        key: 'ctgr_2',
        header: 'CTGR2',
      },
      {
        key: 'ctgr_3',
        header: 'CTGR3',
      },
      // Topic1 - Topic30
      ...Array.from({ length: 30 }, (_, i) => ({
        key: `topic_${i + 1}`,
        header: `Topic${i + 1}`,
      })),
    ];

    if (metrics.length > 0) {
      allColumns = allColumns.filter(item => {
        return metrics.includes(item.key);
      });
    }

    if (!hasChzzk) {
      allColumns = allColumns.filter(item => item.key !== 'live_count');
    }

    return allColumns;
  },
};

// 游戏商店下载配置
export const FeedsGameStoreDownload: DownloadConfigInfoType = {
  type: 'page',
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { hideSentimentRating, isExactOrExtendSearch } = auxiliaryParams;
    // 精准/拓展搜索使用 search 接口，模糊搜索/无搜索使用 comments 接口
    const data = isExactOrExtendSearch
      ? await getFeedsSearchComments(filter)
      : await getFeedsComments(filter);
    const { list = [], total } = data;
    const sentimentMap = await getAllLogValue(
      list,
      {},
      { value_type: ['sentiment', 'topic', 'valid'] },
    );

    const dataList = list.map(
      (item: {
        content: string;
        content_to_en: string;
        content_to_zh: string;
        sentiment_rating: any;
        topic: string;
        isvalid: string;
      }) => {
        const [newSentiment, newTopic, newValid] = [
          'latest_sentiment',
          'latest_topic',
          'latest_valid',
        ].map(key => getLogValue(sentimentMap, item, key as any));

        const sentimentDefaultValue = hideSentimentRating
          ? null
          : item.sentiment_rating;

        // 处理 topic，使用 | 切割后组装成 topic_1 - topic_30
        const finalTopic = newTopic || item.topic || '';
        const topicList = finalTopic.split('|').filter(t => t.trim());
        const topicFields: Record<string, string> = {};
        for (let i = 1; i <= 30; i++) {
          topicFields[`topic_${i}`] = topicList[i - 1] || '';
        }

        return {
          ...item,
          sentiment_rating: newSentiment || sentimentDefaultValue,
          isvalid: newValid || item.isvalid,
          topic: finalTopic,
          ...topicFields,
        };
      },
    );
    return {
      list: dataList,
      total,
    };
  },
  columns: ({ auxiliaryParams, filter }) => {
    const { hideSentimentRating, entity_type: gameType } = auxiliaryParams;
    const { channel } = filter || {};

    // 游戏商店 PC 特有列
    let gameStorePcColumns: any[] = [];
    if (gameType === 'pc') {
      gameStorePcColumns = [
        {
          key: 'refunded',
          header: 'Refunded',
          renderHelper: [
            (item: { [x: string]: string | number }) => {
              if (item?.channel_name === 'steam') {
                return refundedMap[item.refunded];
              }
              return '-';
            },
          ],
        },
        {
          key: 'user_id',
          header: 'SteamId',
          renderHelper: [gameStoreExtJsonHelper('user_id')],
        },
        {
          key: 'unstarred',
          header: 'Starred Tag',
          renderHelper: [
            gameStoreExtJsonHelper('unstarred', dealwithSteamStarred),
          ],
        },
        {
          key: 'received_free',
          header: 'Free Played',
          renderHelper: [
            gameStoreExtJsonHelper('received_free', dealwithSteamStore),
          ],
        },
        {
          key: 'steam_purchase',
          header: 'Steam Purchase',
          renderHelper: [
            gameStoreExtJsonHelper('steam_purchase', dealwithSteamStore),
          ],
        },
        {
          key: 'early_access_reviews',
          header: 'Early Access Reviews',
          renderHelper: [
            gameStoreExtJsonHelper('early_access_reviews', dealwithSteamStore),
          ],
        },
        {
          key: 'funny_num',
          header: 'Funny(people)',
          renderHelper: [gameStoreExtJsonHelper('funny_num')],
        },
        {
          key: 'helpful_num',
          header: 'Helpful(people)',
          renderHelper: [gameStoreExtJsonHelper('helpful_num')],
        },
      ];
    }

    const allColumns = [
      {
        key: 'comment_uin',
        header: 'comment_uin',
      },
      {
        key: 'channel_name',
        header: 'channel',
      },
      {
        key: 'comment_score',
        header: 'rating',
        renderHelper: [gameStoreRating],
      },
      {
        key: 'sentiment_rating',
        header: 'Sentiment',
        renderHelper: [hideSentimentFunc(hideSentimentRating)],
        numFmt: ['0.00'],
      },
      {
        key: 'reviewer',
        header: 'Reviewer',
      },
      {
        key: 'on_record_time',
        header: 'On Record Time(hours)',
        renderHelper: [reviewerPlaytime],
      },
      {
        key: 'at_review_time',
        header: 'At Review Time(hours)',
        renderHelper: [reviewerPlaytime],
      },
      {
        key: 'content',
        header: 'Content_Original',
      },
      {
        key: 'content_to_zh',
        header: 'Content_Chinese',
      },
      {
        key: 'content_to_en',
        header: 'Content_English',
      },
      {
        key: 'isvalid',
        header: 'Validation',
        renderHelper: [renderIsValid],
      },
      {
        key: 'monitor_source',
        header: 'Monitor Source',
        renderHelper: [renderMonitorSource],
      },
      {
        key: 'comment_time',
        header: 'Date',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD')],
      },
      {
        key: 'comment_time',
        header: 'Time(UTC+8:00)',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD HH:mm:ss')],
      },
      {
        key: 'language',
        header: 'Language',
        renderHelper: [languageRenderHelper],
      },
      // PC 特有列
      ...gameStorePcColumns,
      {
        key: 'market',
        header: 'market',
        renderHelper: [regionRenderHelper],
      },
      {
        key: 'version',
        header: 'version',
      },
      {
        key: 'content_url',
        header: 'URL',
      },
      ...Array.from({ length: 30 }, (_, i) => ({
        key: `topic_${i + 1}`,
        header: `Topic${i + 1}`,
      })),
    ];

    // 当 channel 中没有包含 'steam' 时，过滤掉 'On Record Time(hours)' 和 'At Review Time(hours)' 这两列
    const hasSteam =
      Array.isArray(channel) &&
      (channel.length === 0 || channel.includes('steam'));
    if (!hasSteam) {
      return allColumns.filter(
        item =>
          !['On Record Time(hours)', 'At Review Time(hours)']?.includes(
            item.header,
          ),
      );
    }

    return allColumns;
  },
};

// 新闻媒体下载配置
export const FeedsNewsMediaDownload: DownloadConfigInfoType = {
  type: 'page',
  customizeDownloadFunc: async ({ filter, auxiliaryParams }) => {
    const { isExactOrExtendSearch, hideSentimentRating } = auxiliaryParams;
    // 精准/拓展搜索使用 search 接口，模糊搜索/无搜索使用 comments 接口
    const data = isExactOrExtendSearch
      ? await getFeedsSearchComments(filter)
      : await getFeedsComments(filter);
    const { news_detail_info = [], total } = data;

    const sentimentMap = await getAllLogValue(
      news_detail_info.map((item: any) => {
        return {
          ...item,
          comment_uin: item.md5_uni,
        };
      }),
      {},
      { value_type: ['sentiment', 'topic', 'valid'] },
    );

    const dataList = news_detail_info.map(
      (item: {
        content: string;
        content_to_en: string;
        content_to_zh: string;
        sentiment_rating: any;
        topic: string;
        isvalid: string;
      }) => {
        const [newSentiment, newTopic, newValid] = [
          'latest_sentiment',
          'latest_topic',
          'latest_valid',
        ].map(key => getLogValue(sentimentMap, item, key as any));

        const sentimentDefaultValue = hideSentimentRating
          ? null
          : item.sentiment_rating;

        // 处理 topic，使用 | 切割后组装成 topic_1 - topic_30
        const finalTopic = newTopic || item.topic || '';
        const topicList = finalTopic.split('|').filter(t => t.trim());
        const topicFields: Record<string, string> = {};
        for (let i = 1; i <= 30; i++) {
          topicFields[`topic_${i}`] = topicList[i - 1] || '';
        }

        return {
          ...item,
          sentiment_rating: newSentiment || sentimentDefaultValue,
          isvalid: newValid || item.isvalid,
          topic: finalTopic,
          ...topicFields,
        };
      },
    );

    return { list: dataList, total };
  },
  columns: () => {
    return [
      {
        key: 'md5_uni',
        header: 'md5_uni',
        renderHelper: [tryTransNumber],
      },
      {
        key: 'news_url',
        header: 'Url',
      },
      {
        key: 'news_title',
        header: 'Title_Original',
      },
      {
        key: 'title_cn',
        header: 'Title_Chinese',
      },
      {
        key: 'title_en',
        header: 'Title_English',
      },
      {
        key: 'content',
        header: 'Content_Original',
      },
      {
        key: 'content_cn',
        header: 'Content_Chinese',
        renderHelper: [newsBakContent('news_detail_info')],
      },
      {
        key: 'content_en',
        header: 'Content_English',
        renderHelper: [newsBakContent('news_detail_info')],
      },
      // {
      //   key: 'isvalid',
      //   header: 'Validation',
      //   renderHelper: [renderIsValid],
      // },
      {
        key: 'engagement',
        header: 'Engagement',
        numFmt: '#,##',
      },
      {
        key: 'visit_number',
        header: 'Reach',
        numFmt: '#,##',
      },
      {
        key: 'release_time',
        header: 'Published Date',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD')],
      },
      {
        key: 'release_time',
        header: 'Published Time',
        renderHelper: [formatTimeToFormat('YYYY-MM-DD HH:mm:ss')],
      },
      {
        key: 'news_language',
        header: 'Language',
        renderHelper: [languageRenderHelper],
      },
      {
        key: 'market',
        header: 'Region',
        renderHelper: [regionRenderHelper],
      },
      {
        key: 'source',
        header: 'Source',
      },
      {
        key: 'coverage_type',
        header: 'Type of Coverage',
      },
      {
        key: 'bate',
        header: 'Bate',
      },
      ...Array.from({ length: 30 }, (_, i) => ({
        key: `topic_${i + 1}`,
        header: `Topic${i + 1}`,
      })),
    ];
  },
};
